import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLang } from '../../i18n/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell, Plus, Search, RefreshCw, AlertCircle, X, Clock, Calendar,
  Edit3, Ban, Trash2, Bot, Hand, ChevronDown, MessageSquare, Send,
  CheckCircle2, User, Phone,
} from 'lucide-react';
import { reminderService, conversationService } from '../../api/services';
import socketService from '../../api/socket';
import { useToast } from '../../contexts/ToastContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Le backend renvoie { success, data } OU directement data selon le wrapper.
const unwrap = (body) => (body && body.data !== undefined ? body.data : body);

const STATUS_CONFIG = {
  pending:   { label: 'En attente', cls: 'bg-amber-500/20 text-amber-400 border border-amber-500/30' },
  sent:      { label: 'Envoyée',    cls: 'bg-green-500/20 text-green-500 border border-green-500/30' },
  cancelled: { label: 'Annulée',    cls: 'bg-gray-500/20 text-gray-400 border border-gray-500/30' },
  failed:    { label: 'Échec',      cls: 'bg-red-500/20 text-red-500 border border-red-500/30' },
};

const FILTERS = [
  { id: 'all', label: 'Toutes' },
  { id: 'pending', label: 'En attente' },
  { id: 'sent', label: 'Envoyées' },
  { id: 'cancelled', label: 'Annulées' },
  { id: 'failed', label: 'Échecs' },
];

const formatDateTime = (iso, lang = 'fr') => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const locale = lang === 'en' ? 'en-US' : 'fr-FR';
  return d.toLocaleString(locale, {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
};

const relativeTime = (iso, tc) => {
  const ts = new Date(iso).getTime();
  if (isNaN(ts)) return '';
  const diff = ts - Date.now();
  const abs = Math.abs(diff);
  const MIN = 60000, HOUR = 3600000, DAY = 86400000;
  const future = diff >= 0;
  if (abs < MIN) return future ? tc.inMoment : tc.justNow;
  let val;
  if (abs < HOUR) { val = Math.round(abs / MIN); return future ? tc.inMinutes(val) : tc.relativeMin(val); }
  if (abs < DAY)  { val = Math.round(abs / HOUR); return future ? tc.inHours(val) : tc.relativeHour(val); }
  val = Math.round(abs / DAY);
  return future ? tc.inDays(val) : tc.relativeDay(val);
};

// triggerAt dans moins de 24h (et pas encore passé) → mise en avant
const isSoon = (iso) => {
  const diff = new Date(iso).getTime() - Date.now();
  return diff >= 0 && diff < 24 * 3600000;
};
const isOverdue = (iso) => new Date(iso).getTime() < Date.now();

// ISO → valeur d'un <input type="datetime-local"> en heure locale
const toLocalInputValue = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Relances() {
  const { t, lang } = useLang();

  const translatedFilters = [
    { id: 'all', label: t.relances.filterAll },
    { id: 'pending', label: t.relances.pending },
    { id: 'sent', label: t.relances.filterSent },
    { id: 'cancelled', label: t.relances.filterCancelled },
    { id: 'failed', label: t.relances.filterFailed },
  ];
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const qc = useQueryClient();

  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [highlightIds, setHighlightIds] = useState(() => new Set());

  // Modal : { mode: 'create' | 'edit', reminder } ou null
  const [modal, setModal] = useState(null);

  // Clé stable accessible dans les handlers socket (évite les closures périmées)
  const queryKey = useMemo(() => ['reminders', fromDate, toDate], [fromDate, toDate]);
  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;

  // ── Chargement (filtre serveur sur la plage de dates uniquement ;
  //    le statut est filtré côté client pour des onglets instantanés + temps réel) ──
  const { data: remindersRaw, isLoading: loading, isError, error: fetchError, refetch: refetchReminders, isFetching } = useQuery({
    queryKey,
    queryFn: () => {
      const params = {};
      if (fromDate) params.from = new Date(fromDate).toISOString();
      if (toDate) params.to = new Date(toDate).toISOString();
      return reminderService.getReminders(params);
    },
  });

  const reminders = useMemo(() => {
    const data = unwrap(remindersRaw);
    return Array.isArray(data) ? data : [];
  }, [remindersRaw]);

  // ── Conversations (depuis le cache partagé avec ConversationsConnected) ──────
  const { data: convsRaw } = useQuery({
    queryKey: ['conversations', 'all'],
    queryFn: () => conversationService.getConversations({}),
  });

  const conversations = useMemo(() => {
    if (!convsRaw) return [];
    if (convsRaw?.success && Array.isArray(convsRaw.data)) return convsRaw.data;
    const data = unwrap(convsRaw);
    return Array.isArray(data) ? data : [];
  }, [convsRaw]);

  // ── Helper : patch du cache sans re-fetch ───────────────────────────────────
  const patchReminders = useCallback((fn) => {
    qc.setQueryData(queryKeyRef.current, (prev) => {
      if (!prev) return prev;
      if (prev && typeof prev === 'object' && Array.isArray(prev.data)) {
        return { ...prev, data: fn(prev.data) };
      }
      if (Array.isArray(prev)) return fn(prev);
      return prev;
    });
  }, [qc]);

  // ── Temps réel ──────────────────────────────────────────────────────────────
  const flashHighlight = useCallback((id) => {
    setHighlightIds((prev) => new Set(prev).add(id));
    setTimeout(() => {
      setHighlightIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 4000);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token && !socketService.isConnected()) socketService.connect(token);

    const onCreated = (payload) => {
      const r = unwrap(payload);
      if (!r?._id) return;
      patchReminders((arr) => (arr.some((x) => x._id === r._id) ? arr : [...arr, r]));
      flashHighlight(r._id);
      toastRef.current.info(
        t.relances.toastCreated(r.clientName || t.relances.client, formatDateTime(r.triggerAt, lang)),
        { title: t.relances.toastCreatedTitle }
      );
    };
    const onUpdated = (payload) => {
      const r = unwrap(payload);
      if (!r?._id) return;
      patchReminders((arr) => arr.map((x) => (x._id === r._id ? { ...x, ...r } : x)));
    };
    const onSent = (payload) => {
      const r = unwrap(payload);
      if (!r?._id) return;
      patchReminders((arr) => arr.map((x) => (x._id === r._id ? { ...x, ...r, status: 'sent' } : x)));
      flashHighlight(r._id);
      toastRef.current.success(t.relances.toastSent(r.clientName || t.relances.client), {
        title: t.relances.toastSentTitle,
      });
    };
    const onDeleted = (payload) => {
      const r = unwrap(payload);
      const id = r?._id || r?.id || payload;
      patchReminders((arr) => arr.filter((x) => x._id !== id));
    };

    socketService.on('reminder:created', onCreated);
    socketService.on('reminder:updated', onUpdated);
    socketService.on('reminder:sent', onSent);
    socketService.on('reminder:deleted', onDeleted);

    return () => {
      socketService.off('reminder:created', onCreated);
      socketService.off('reminder:updated', onUpdated);
      socketService.off('reminder:sent', onSent);
      socketService.off('reminder:deleted', onDeleted);
    };
  }, [flashHighlight, patchReminders]);

  // ── Filtrage + tri ──────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return reminders
      .filter((r) => {
        if (filterStatus !== 'all' && r.status !== filterStatus) return false;
        if (q) {
          const hay = `${r.clientName || ''} ${r.clientPhone || ''} ${r.reason || ''}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(a.triggerAt) - new Date(b.triggerAt));
  }, [reminders, filterStatus, search]);

  // Compteurs par statut (badges d'onglets)
  const counts = useMemo(() => {
    const c = { all: reminders.length, pending: 0, sent: 0, cancelled: 0, failed: 0 };
    reminders.forEach((r) => { if (c[r.status] !== undefined) c[r.status] += 1; });
    return c;
  }, [reminders]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleCancel = useCallback(
    async (reminder) => {
      if (!window.confirm(t.relances.cancelConfirm(reminder.clientName || t.relances.client))) return;
      try {
        const body = await reminderService.cancelReminder(reminder._id);
        const updated = unwrap(body) || { ...reminder, status: 'cancelled' };
        patchReminders((arr) => arr.map((x) => (x._id === reminder._id ? { ...x, ...updated } : x)));
        toast.success(t.relances.cancelSuccess);
      } catch (err) {
        toast.error(err.message || t.relances.cancelError);
      }
    },
    [toast, patchReminders]
  );

  const handleDelete = useCallback(
    async (reminder) => {
      if (!window.confirm(t.relances.deleteConfirm)) return;
      try {
        await reminderService.deleteReminder(reminder._id);
        patchReminders((arr) => arr.filter((x) => x._id !== reminder._id));
        toast.success(t.relances.deleteSuccess);
      } catch (err) {
        toast.error(err.message || t.relances.deleteError2);
      }
    },
    [toast, patchReminders]
  );

  const openCreate = useCallback(() => {
    setModal({ mode: 'create', reminder: null });
  }, []);

  const openEdit = useCallback((reminder) => {
    setModal({ mode: 'edit', reminder });
  }, []);

  const handleModalSubmit = useCallback(
    async (payload) => {
      try {
        if (modal.mode === 'create') {
          const body = await reminderService.createReminder(payload);
          const created = unwrap(body);
          if (created?._id) {
            patchReminders((arr) => (arr.some((x) => x._id === created._id) ? arr : [...arr, created]));
            flashHighlight(created._id);
          }
          toast.success(t.relances.createSuccess);
        } else {
          const body = await reminderService.updateReminder(modal.reminder._id, payload);
          const updated = unwrap(body) || { ...modal.reminder, ...payload };
          patchReminders((arr) => arr.map((x) => (x._id === modal.reminder._id ? { ...x, ...updated } : x)));
          toast.success(t.relances.editSuccess);
        }
        setModal(null);
      } catch (err) {
        toast.error(err.message || t.relances.opError);
      }
    },
    [modal, toast, flashHighlight, patchReminders]
  );

  // ── États ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-10 w-10 mx-auto mb-4"
            style={{ borderWidth: '2px', borderStyle: 'solid', borderColor: 'var(--c-border)', borderTopColor: 'rgb(var(--c-accent-green-rgb))' }}
          />
          <p className="text-label">{t.relances.loading}</p>
        </div>
      </div>
    );
  }

  if (isError && !remindersRaw) {
    return (
      <div className="card p-5 mb-4" style={{ borderColor: 'rgba(224,84,84,0.3)' }}>
        <div className="flex items-start gap-3">
          <AlertCircle size={16} style={{ color: 'var(--c-error)', flexShrink: 0, marginTop: 2 }} />
          <div>
            <p className="text-sm font-medium" style={{ color: 'var(--c-error)' }}>{t.relances.errorTitle}</p>
            <p className="text-label mt-1">{fetchError?.message}</p>
            <button onClick={() => refetchReminders()} className="btn-primary mt-3" style={{ height: '32px', fontSize: '12px' }}>
              <RefreshCw size={13} /> {t.common.retry}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* ── En-tête ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs2 mb-1" style={{ color: 'var(--c-text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {t.relances.titleFull}
          </p>
          <div className="flex items-center gap-2">
            <h1 className="text-ink font-semibold" style={{ fontSize: '18px' }}>
              {t.relances.subtitleFull}
            </h1>
            {isFetching && !loading && (
              <RefreshCw size={13} className="animate-spin" style={{ color: 'var(--c-text-muted)' }} />
            )}
          </div>
        </div>
        <button className="btn-primary" onClick={openCreate}>
          <Plus size={14} strokeWidth={2} />
          {t.relances.newRelance}
        </button>
      </div>

      {/* ── Métriques ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: t.relances.statTotal, value: counts.all, icon: Bell },
          { label: t.relances.pending, value: counts.pending, icon: Clock },
          { label: t.relances.statSent, value: counts.sent, icon: Send },
          { label: t.relances.statCancelled, value: counts.cancelled, icon: Ban },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06, duration: 0.3, ease: 'easeOut' }}
              className="card-metric p-5"
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center mb-3"
                style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}
              >
                <Icon size={18} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} strokeWidth={1.5} />
              </div>
              <p className="text-label mb-1">{s.label}</p>
              <p className="font-metric text-ink" style={{ fontSize: '28px', fontWeight: 600, lineHeight: 1 }}>
                {s.value}
              </p>
            </motion.div>
          );
        })}
      </div>

      {/* ── Recherche + filtres ─────────────────────────────────────────────── */}
      <div className="card p-4 mb-6 space-y-3">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1 relative">
            <Search
              size={14} strokeWidth={1.5}
              className="absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--c-text-muted)' }}
            />
            <input
              type="text"
              placeholder={t.relances.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full text-sm pl-9 pr-3 py-2 rounded-lg outline-none text-ink"
              style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
              onFocus={e => (e.target.style.borderColor = 'rgb(var(--c-accent-green-rgb))')}
              onBlur={e => (e.target.style.borderColor = 'var(--c-border)')}
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-label text-xs">{t.relances.from}</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="text-ink text-sm px-3 py-2 rounded-lg outline-none"
              style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
            />
            <label className="text-label text-xs">{t.relances.to}</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="text-ink text-sm px-3 py-2 rounded-lg outline-none"
              style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
            />
            {(fromDate || toDate) && (
              <button
                onClick={() => { setFromDate(''); setToDate(''); }}
                className="p-1.5 rounded-lg transition-all"
                style={{ color: 'var(--c-text-muted)' }}
                title={t.relances.clearDates}
              >
                <X size={14} />
              </button>
            )}
            <button
              onClick={() => refetchReminders()}
              className="p-2 rounded-lg transition-all hover:bg-[var(--c-hover)]"
              title="Rafraîchir"
            >
              <RefreshCw size={14} strokeWidth={1.5} className={isFetching ? 'animate-spin' : ''} style={{ color: 'var(--c-text-muted)' }} />
            </button>
          </div>
        </div>

        {/* Onglets de statut */}
        <div className="flex gap-2 flex-wrap">
          {translatedFilters.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilterStatus(f.id)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5"
              style={{
                background: filterStatus === f.id ? 'rgb(var(--c-accent-green-rgb))' : 'var(--c-hover)',
                color: filterStatus === f.id ? '#fff' : 'var(--c-text-gray)',
              }}
            >
              {f.label}
              <span style={{ opacity: 0.7 }}>{counts[f.id] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Liste ───────────────────────────────────────────────────────────── */}
      {filtered.length === 0 ? (
        <div className="card p-8 sm:p-12 text-center">
          <Bell className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--c-text-muted)' }} />
          <h3 className="text-ink font-semibold mb-2" style={{ fontSize: '16px' }}>{t.relances.noRelances}</h3>
          <p className="text-label max-w-md mx-auto">{t.relances.noRelancesDesc}</p>
        </div>
      ) : (
        <>
        {/* Tableau — desktop / tablette */}
        <div className="hidden md:block card overflow-x-auto">
          <table className="w-full min-w-[860px]">
            <thead style={{ background: 'var(--c-primary-sidebar)' }}>
              <tr style={{ borderBottom: '0.5px solid var(--c-border)' }}>
                <th className="text-left p-4 text-label font-medium">{t.relances.tableClient}</th>
                <th className="text-left p-4 text-label font-medium">{t.relances.tableWhen}</th>
                <th className="text-left p-4 text-label font-medium">{t.relances.tableReason}</th>
                <th className="text-left p-4 text-label font-medium">{t.relances.tableSource}</th>
                <th className="text-left p-4 text-label font-medium">{t.relances.tableStatus}</th>
                <th className="text-right p-4 text-label font-medium">{t.relances.tableActions}</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence initial={false}>
                {filtered.map((r) => (
                  <ReminderRow
                    key={r._id}
                    reminder={r}
                    expanded={expandedId === r._id}
                    highlighted={highlightIds.has(r._id)}
                    onToggle={() => setExpandedId(expandedId === r._id ? null : r._id)}
                    onEdit={() => openEdit(r)}
                    onCancel={() => handleCancel(r)}
                    onDelete={() => handleDelete(r)}
                  />
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {/* Cartes — mobile */}
        <div className="md:hidden space-y-3">
          {filtered.map((r) => (
            <ReminderCard
              key={r._id}
              reminder={r}
              expanded={expandedId === r._id}
              highlighted={highlightIds.has(r._id)}
              onToggle={() => setExpandedId(expandedId === r._id ? null : r._id)}
              onEdit={() => openEdit(r)}
              onCancel={() => handleCancel(r)}
              onDelete={() => handleDelete(r)}
            />
          ))}
        </div>
        </>
      )}

      {/* ── Modal création / reprogrammation ────────────────────────────────── */}
      <AnimatePresence>
        {modal && (
          <ReminderModal
            mode={modal.mode}
            reminder={modal.reminder}
            conversations={conversations}
            onClose={() => setModal(null)}
            onSubmit={handleModalSubmit}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Ligne de relance ─────────────────────────────────────────────────────────

function ReminderRow({ reminder: r, expanded, highlighted, onToggle, onEdit, onCancel, onDelete }) {
  const { t, lang } = useLang();
  const statusCfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
  const statusLabel = t.relances[r.status] || statusCfg.label;
  const isAgent = r.source === 'agent';
  const soon = r.status === 'pending' && isSoon(r.triggerAt);
  const overdue = r.status === 'pending' && isOverdue(r.triggerAt);
  const canManage = r.status === 'pending';

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 8 }}
        animate={{
          opacity: 1,
          y: 0,
          boxShadow: highlighted ? 'inset 0 0 0 2px rgba(29,191,115,0.6)' : 'inset 0 0 0 0 transparent',
        }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onToggle}
        className={`transition-colors cursor-pointer hover:bg-[var(--c-hover)] ${soon ? 'bg-amber-500/[0.04]' : ''}`}
        style={{ borderTop: '0.5px solid var(--c-border)' }}
      >
        {/* Client */}
        <td className="p-4">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(var(--c-accent-green-rgb), 0.1)' }}
            >
              <User size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
            </div>
            <div className="min-w-0">
              <p className="text-ink font-medium truncate">{r.clientName || 'Client'}</p>
              {r.clientPhone && (
                <p className="text-label text-xs flex items-center gap-1">
                  <Phone size={10} /> {r.clientPhone}
                </p>
              )}
            </div>
          </div>
        </td>

        {/* Quand */}
        <td className="p-4">
          <div className="flex items-center gap-1.5">
            <Calendar size={13} className={soon ? 'text-amber-400' : 'text-label'} />
            <span className={`text-sm ${soon ? 'text-amber-300 font-semibold' : 'text-ink'}`}>
              {formatDateTime(r.triggerAt, lang)}
            </span>
          </div>
          <p className={`text-xs mt-0.5 flex items-center gap-1 ${
            overdue ? 'text-red-400' : soon ? 'text-amber-400' : 'text-label'
          }`}>
            <Clock size={10} />
            {overdue && r.status === 'pending' ? `${t.relances.overdueLabel} (${relativeTime(r.triggerAt, t.common)})` : relativeTime(r.triggerAt, t.common)}
          </p>
        </td>

        {/* Raison */}
        <td className="p-4 max-w-[220px]">
          <p className="text-ink text-sm truncate">{r.reason || '—'}</p>
          {r.contextSummary && (
            <span className="text-label text-xs flex items-center gap-1 mt-0.5">
              <ChevronDown
                size={11}
                className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
              />
              {expanded ? t.relances.hideContext : t.relances.viewContext}
            </span>
          )}
        </td>

        {/* Source */}
        <td className="p-4">
          {isAgent ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-md text-xs font-semibold whitespace-nowrap">
              <Bot size={11} /> {t.relances.ai}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/15 text-blue-400 border border-blue-500/30 rounded-md text-xs font-semibold whitespace-nowrap">
              <Hand size={11} /> {t.relances.manual}
            </span>
          )}
        </td>

        {/* Statut */}
        <td className="p-4">
          <span className={`px-3 py-1 rounded-full text-xs ${statusCfg.cls}`}>
            {statusLabel}
          </span>
        </td>

        {/* Actions */}
        <td className="p-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end gap-1">
            {canManage ? (
              <>
                <motion.button
                  onClick={onEdit}
                  whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                  className="p-2 hover:bg-blue-500/20 rounded-lg text-blue-400 transition-all"
                  title={t.relances.tooltipReschedule}
                >
                  <Edit3 size={16} />
                </motion.button>
                <motion.button
                  onClick={onCancel}
                  whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                  className="p-2 hover:bg-amber-500/20 rounded-lg text-amber-400 transition-all"
                  title={t.relances.tooltipCancel}
                >
                  <Ban size={16} />
                </motion.button>
                <motion.button
                  onClick={onDelete}
                  whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                  className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 transition-all"
                  title={t.relances.tooltipDelete}
                >
                  <Trash2 size={16} />
                </motion.button>
              </>
            ) : (
              <motion.button
                onClick={onToggle}
                whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                className="p-2 hover:bg-accent-green/20 rounded-lg text-accent-green transition-all"
                title={t.relances.tooltipDetails}
              >
                <ChevronDown size={16} className={expanded ? 'rotate-180 transition-transform' : 'transition-transform'} />
              </motion.button>
            )}
          </div>
        </td>
      </motion.tr>

      {/* Ligne détail (contexte + message envoyé) */}
      <AnimatePresence>
        {expanded && (
          <motion.tr
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <td colSpan={6} className="px-4 pb-4" style={{ background: 'var(--c-hover)' }}>
              <div className="rounded-lg p-4 space-y-3" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
                {r.contextSummary && (
                  <div>
                    <p className="text-label text-xs font-semibold mb-1 flex items-center gap-1.5">
                      <MessageSquare size={12} /> {t.relances.contextLabel}
                    </p>
                    <p className="text-ink text-sm whitespace-pre-wrap">{r.contextSummary}</p>
                  </div>
                )}

                {r.status === 'sent' && (
                  <div className="pt-2" style={{ borderTop: '0.5px solid var(--c-border)' }}>
                    <p className="text-emerald-400 text-xs font-semibold mb-1 flex items-center gap-1.5">
                      <Send size={12} /> {t.relances.sentMessage}
                      {r.sentAt && (
                        <span className="text-label font-normal flex items-center gap-1 ml-2">
                          <CheckCircle2 size={11} className="text-green-500" />
                          {formatDateTime(r.sentAt, lang)}
                        </span>
                      )}
                    </p>
                    <p className="text-ink text-sm whitespace-pre-wrap bg-emerald-900/10 border border-emerald-500/15 rounded-lg p-3">
                      {r.generatedMessage || t.relances.msgUnavailable}
                    </p>
                  </div>
                )}

                {typeof r.attempts === 'number' && r.attempts > 0 && (
                  <p className="text-label text-xs">{t.relances.attempts(r.attempts)}</p>
                )}

                {!r.contextSummary && r.status !== 'sent' && (
                  <p className="text-label text-sm italic">{t.relances.noDetails}</p>
                )}
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
}

// ─── Carte de relance (mobile) ────────────────────────────────────────────────

function ReminderCard({ reminder: r, expanded, highlighted, onToggle, onEdit, onCancel, onDelete }) {
  const { t, lang } = useLang();
  const statusCfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
  const statusLabel = t.relances[r.status] || statusCfg.label;
  const isAgent = r.source === 'agent';
  const soon = r.status === 'pending' && isSoon(r.triggerAt);
  const overdue = r.status === 'pending' && isOverdue(r.triggerAt);
  const canManage = r.status === 'pending';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{
        opacity: 1,
        y: 0,
        boxShadow: highlighted ? 'inset 0 0 0 2px rgba(29,191,115,0.6)' : 'inset 0 0 0 0 transparent',
      }}
      exit={{ opacity: 0 }}
      className={`card p-4 ${soon ? 'bg-amber-500/[0.04]' : ''}`}
    >
      {/* En-tête : client + statut */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(var(--c-accent-green-rgb), 0.1)' }}
          >
            <User size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
          </div>
          <div className="min-w-0">
            <p className="text-ink font-medium truncate">{r.clientName || 'Client'}</p>
            {r.clientPhone && (
              <p className="text-label text-xs flex items-center gap-1 truncate">
                <Phone size={10} /> {r.clientPhone}
              </p>
            )}
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs flex-shrink-0 ${statusCfg.cls}`}>
          {statusLabel}
        </span>
      </div>

      {/* Quand */}
      <div className="flex items-center gap-1.5 mb-1">
        <Calendar size={13} className={soon ? 'text-amber-400' : 'text-label'} />
        <span className={`text-sm ${soon ? 'text-amber-300 font-semibold' : 'text-ink'}`}>
          {formatDateTime(r.triggerAt, lang)}
        </span>
      </div>
      <p className={`text-xs mb-3 flex items-center gap-1 ${
        overdue ? 'text-red-400' : soon ? 'text-amber-400' : 'text-label'
      }`}>
        <Clock size={10} />
        {overdue && r.status === 'pending' ? `${t.relances.overdueLabel} (${relativeTime(r.triggerAt, t.common)})` : relativeTime(r.triggerAt, t.common)}
      </p>

      {/* Raison + source */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {isAgent ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-md text-xs font-semibold">
            <Bot size={11} /> {t.relances.ai}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/15 text-blue-400 border border-blue-500/30 rounded-md text-xs font-semibold">
            <Hand size={11} /> {t.relances.manual}
          </span>
        )}
        <p className="text-ink text-sm min-w-0 flex-1 truncate">{r.reason || '—'}</p>
      </div>

      {/* Contexte dépliable */}
      {(r.contextSummary || r.status === 'sent') && (
        <button
          onClick={onToggle}
          className="text-label text-xs flex items-center gap-1 mb-2"
        >
          <ChevronDown size={12} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
          {expanded ? t.relances.hideDetails : t.relances.viewDetails}
        </button>
      )}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="rounded-lg p-3 space-y-3 mb-3" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
              {r.contextSummary && (
                <div>
                  <p className="text-label text-xs font-semibold mb-1 flex items-center gap-1.5">
                    <MessageSquare size={12} /> {t.relances.contextLabel}
                  </p>
                  <p className="text-ink text-sm whitespace-pre-wrap">{r.contextSummary}</p>
                </div>
              )}
              {r.status === 'sent' && (
                <div className="pt-2" style={{ borderTop: '0.5px solid var(--c-border)' }}>
                  <p className="text-emerald-400 text-xs font-semibold mb-1 flex items-center gap-1.5">
                    <Send size={12} /> {t.relances.sentMessage}
                  </p>
                  <p className="text-ink text-sm whitespace-pre-wrap bg-emerald-900/10 border border-emerald-500/15 rounded-lg p-3">
                    {r.generatedMessage || t.relances.msgUnavailable}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Actions */}
      {canManage && (
        <div className="flex items-center gap-1 pt-2" style={{ borderTop: '0.5px solid var(--c-border)' }}>
          <button onClick={onEdit} className="flex-1 flex items-center justify-center gap-1.5 py-2 hover:bg-blue-500/20 rounded-lg text-blue-400 text-sm transition-all">
            <Edit3 size={15} /> {t.relances.rescheduleBtn}
          </button>
          <button onClick={onCancel} className="flex items-center justify-center gap-1.5 px-3 py-2 hover:bg-amber-500/20 rounded-lg text-amber-400 text-sm transition-all">
            <Ban size={15} />
          </button>
          <button onClick={onDelete} className="flex items-center justify-center gap-1.5 px-3 py-2 hover:bg-red-500/20 rounded-lg text-red-400 text-sm transition-all">
            <Trash2 size={15} />
          </button>
        </div>
      )}
    </motion.div>
  );
}

// ─── Modal création / reprogrammation ─────────────────────────────────────────

function ReminderModal({ mode, reminder, conversations, onClose, onSubmit }) {
  const { t } = useLang();
  const isEdit = mode === 'edit';
  const [conversationId, setConversationId] = useState(reminder?.conversationId || '');
  const [triggerAt, setTriggerAt] = useState(toLocalInputValue(reminder?.triggerAt));
  const [reason, setReason] = useState(reminder?.reason || '');
  const [contextSummary, setContextSummary] = useState(reminder?.contextSummary || '');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setFormError('');

    if (!isEdit && !conversationId) {
      setFormError(t.relances.errNoConv);
      return;
    }
    if (!triggerAt) {
      setFormError(t.relances.errNoDate);
      return;
    }
    const when = new Date(triggerAt);
    if (isNaN(when.getTime()) || when.getTime() <= Date.now()) {
      setFormError(t.relances.errFutureDate);
      return;
    }
    if (!reason.trim()) {
      setFormError(t.relances.errNoReason);
      return;
    }

    const payload = isEdit
      ? {
          triggerAt: when.toISOString(),
          reason: reason.trim(),
          contextSummary: contextSummary.trim() || undefined,
        }
      : {
          conversationId,
          triggerAt: when.toISOString(),
          reason: reason.trim(),
          contextSummary: contextSummary.trim() || undefined,
          clientName:
            conversations.find((c) => c._id === conversationId)?.clientName || undefined,
        };

    setSubmitting(true);
    await onSubmit(payload);
    setSubmitting(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="card rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 flex items-center justify-between" style={{ borderBottom: '0.5px solid var(--c-border)', background: 'var(--c-primary-dark)' }}>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}>
              <Bell style={{ color: 'rgb(var(--c-accent-green-rgb))' }} size={22} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-ink">
                {isEdit ? t.relances.reschedule : t.relances.scheduleNew}
              </h2>
              <p className="text-label text-xs">
                {isEdit ? reminder?.clientName : t.relances.scheduleFor}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--c-hover)] rounded-lg text-label hover:text-ink transition-all">
            <X size={20} />
          </button>
        </div>

        {/* Corps */}
        <div className="p-5 space-y-4">
          {!isEdit && (
            <div>
              <label className="block text-ink text-sm font-medium mb-1.5">{t.relances.convLabel}</label>
              <select
                value={conversationId}
                onChange={(e) => setConversationId(e.target.value)}
                className="w-full text-ink px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-green"
                style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
              >
                <option value="">{t.relances.selectConv}</option>
                {conversations.map((c) => (
                  <option key={c._id} value={c._id}>
                    {(c.clientName || t.relances.client)}{c.clientPhone ? ` · ${c.clientPhone}` : ''}
                  </option>
                ))}
              </select>
              {conversations.length === 0 && (
                <p className="text-label text-xs mt-1">{t.relances.noConv}</p>
              )}
            </div>
          )}

          <div>
            <label className="block text-ink text-sm font-medium mb-1.5">{t.relances.dateLabel}</label>
            <input
              type="datetime-local"
              value={triggerAt}
              min={toLocalInputValue(new Date(Date.now() + 60000).toISOString())}
              onChange={(e) => setTriggerAt(e.target.value)}
              className="w-full text-ink px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-green"
                style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
            />
          </div>

          <div>
            <label className="block text-ink text-sm font-medium mb-1.5">{t.relances.reasonLabel}</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t.relances.reasonPlaceholder}
              className="w-full text-ink px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-green"
                style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
            />
          </div>

          <div>
            <label className="block text-ink text-sm font-medium mb-1.5">
              {t.relances.contextOptLabel} <span className="text-label font-normal">({t.relances.optional})</span>
            </label>
            <textarea
              value={contextSummary}
              onChange={(e) => setContextSummary(e.target.value)}
              rows={3}
              placeholder={t.relances.contextPlaceholder}
              className="w-full text-ink px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-green resize-none"
              style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
            />
          </div>

          {formError && (
            <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-2.5">
              <AlertCircle size={16} className="flex-shrink-0" />
              {formError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 flex gap-3" style={{ borderTop: '0.5px solid var(--c-border)' }}>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-label hover:text-ink rounded-lg font-medium transition-colors hover:bg-[var(--c-hover)]"
            style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
          >
            {t.common.cancel}
          </button>
          <motion.button
            onClick={handleSubmit}
            disabled={submitting}
            whileHover={{ scale: submitting ? 1 : 1.02 }}
            whileTap={{ scale: submitting ? 1 : 0.98 }}
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-accent-green to-accent-green/80 text-black rounded-lg font-bold flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
          >
            {submitting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black" />
            ) : (
              <>{isEdit ? <Edit3 size={16} /> : <Plus size={16} />} {isEdit ? t.relances.rescheduleBtn : t.relances.scheduleBtn}</>
            )}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
